import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Information, User, ArrowRight } from '@carbon/icons-react';
import axiosInstance from '../utils/axiosInstance';
import './ActionNeeded.css';

// Backend šalje opis problema (npr. "Korisnik nema dodeljenu ishranu"), a panel
// "Akcije" prikazuje šta treba uraditi, zajedno sa dugmetom konkretne akcije.
const ACTION_TYPE_UI = {
  ResidentHasNoDiet: {
    title: 'Dodeliti plan ishrane',
    actionLabel: 'Dodeli ishranu',
  },
  ResidentHasNoHealthRecord: {
    title: 'Kreirati zdravstveni karton',
    actionLabel: 'Kreiraj karton',
  },
  ResidentSuppliesDepleting: {
    title: 'Dopuniti zalihe korisniku',
    actionLabel: 'Dopuni zalihe',
  },
  EmployeeHasNoShifts: {
    title: 'Dodeliti smene zaposlenom',
    actionLabel: 'Dodeli smene',
  },
  DietPlanMissingMeals: {
    title: 'Dodati obroke u jelovnik',
    actionLabel: 'Dodaj obroke',
  },
};

// Redosled prikaza po važnosti (Critical pre Warning). Backend ih već sortira,
// ali obezbeđujemo stabilan prikaz i na klijentu.
const SEVERITY_ORDER = { Critical: 0, Warning: 1 };

const severityClass = (severity) => {
  if (severity === 'Critical') return 'critical';
  if (severity === 'Warning') return 'warning';
  return 'info';
};

// Naslov stavke i tekst dugmeta za dati tip akcije (uz bezbedan fallback)
const getActionUi = (action) => {
  const mapped = ACTION_TYPE_UI[action?.actionType];
  return {
    title: mapped?.title || action?.actionTypeName || 'Potrebna akcija',
    actionLabel: mapped?.actionLabel || 'Otvori',
  };
};

// Odredište na koje vodi klik na akciju, u zavisnosti od tipa entiteta.
// Vraća putanju ili null ako akcija nije klikabilna.
const getActionTarget = (action) => {
  if (!action) return null;
  if (action.entityType === 'Resident' && action.entityId) {
    return `/residents/${action.entityId}`;
  }
  if (action.entityType === 'Employee' && action.entityId) {
    // Profil zaposlenog, otvoren na tabu "Smene"
    return `/employees/${action.entityId}?tab=shifts`;
  }
  if (action.entityType === 'DietPlan') {
    // Jelovnik sa unapred izabranim tipom ishrane u filteru
    const name = action.entityTypeName || '';
    return `/jelovnik?dietPlan=${encodeURIComponent(name)}`;
  }
  return null;
};

// Izvlači niz iz različitih oblika odgovora
const extractArray = (res) => {
  const body = res.data;
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.data)) return body.data;
  if (body && Array.isArray(body.value)) return body.value;
  if (body && Array.isArray(body.$values)) return body.$values;
  if (body && Array.isArray(body.items)) return body.items;
  if (body && body.data && Array.isArray(body.data.$values)) return body.data.$values;
  if (body && body.data && Array.isArray(body.data.items)) return body.data.items;
  return [];
};

const ActionNeeded = () => {
  const navigate = useNavigate();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchActions = async () => {
      try {
        setLoading(true);
        setError(null);
        // Uvek prikazujemo samo nerešene akcije
        const response = await axiosInstance.get('/api/ActionNeeded', {
          params: { unresolvedOnly: true },
        });
        if (isMounted) setActions(extractArray(response));
      } catch (err) {
        // 404 (nema akcija) nije greška - prikaži prazno stanje
        if (err?.response?.status === 404) {
          if (isMounted) setActions([]);
        } else {
          console.error('Greška pri učitavanju potrebnih akcija:', err);
          if (isMounted) setError('Greška pri učitavanju akcija');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchActions();
    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return (
      <section className="action-needed" aria-busy="true">
        {/* Skeleton zaglavlja ("Potrebne akcije") */}
        <header className="action-needed__head">
          <span className="action-needed__skeleton-line action-needed__skeleton-line--title" />
          <span className="action-needed__skeleton-line action-needed__skeleton-line--sub" />
        </header>
        <ul className="action-needed__list">
          {[0, 1].map((i) => (
            <li className="action-needed__row action-needed__row--skeleton" key={i}>
              <span className="action-needed__skeleton-icon" />
              <div className="action-needed__row-body">
                <span className="action-needed__skeleton-line" style={{ width: '9rem' }} />
                <span
                  className="action-needed__skeleton-line action-needed__skeleton-line--sm"
                  style={{ width: '6rem' }}
                />
              </div>
              <span className="action-needed__skeleton-button" />
            </li>
          ))}
        </ul>
      </section>
    );
  }

  if (error) {
    return (
      <section className="action-needed">
        <div className="action-needed__empty">
          <span>{error}</span>
        </div>
      </section>
    );
  }

  // Kritične akcije idu prve (stabilan redosled)
  const sortedActions = [...actions].sort(
    (a, b) => (SEVERITY_ORDER[a.severity] ?? 99) - (SEVERITY_ORDER[b.severity] ?? 99)
  );

  return (
    <section className="action-needed">
      <header className="action-needed__head">
        <div className="action-needed__head-row">
          <h2 className="action-needed__title">Potrebne akcije</h2>
          {actions.length > 0 && (
            <span className="action-needed__count">{actions.length}</span>
          )}
        </div>
        <p className="action-needed__subtitle">Dopunite podatke korisnika.</p>
      </header>

      {sortedActions.length === 0 ? (
        <div className="action-needed__empty">
          <span>Nema potrebnih akcija</span>
        </div>
      ) : (
        <ul className="action-needed__list">
          {sortedActions.map((action, index) => {
            const ui = getActionUi(action);
            const target = getActionTarget(action);
            return (
              <li
                className="action-needed__row"
                key={action.id || `${action.actionType}-${index}`}
              >
                <Information
                  size={24}
                  className={`action-needed__row-icon action-needed__row-icon--${severityClass(
                    action.severity
                  )}`}
                />
                <div className="action-needed__row-body">
                  <span className="action-needed__row-title">{ui.title}</span>
                  {action.entityTypeName && (
                    <span className="action-needed__row-meta">
                      <User size={16} />
                      {action.entityTypeName}
                    </span>
                  )}
                </div>
                {target && (
                  <button
                    type="button"
                    className="action-needed__row-action"
                    onClick={() => navigate(target)}
                  >
                    <span>{ui.actionLabel}</span>
                    <ArrowRight size={16} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default ActionNeeded;

